const ratesCache = new Map();
const ratesScriptCache = new Map();
const VARIANT_UPDATE_EVENT = 'variant:update';
const FALLBACK_RATES = {
  base: 'USD',
  updatedAt: '2026-09-01T06:09:52.722Z',
  source: 'theme-fallback',
  ttlHours: 72,
  rates: {
    AUD: 1.3963,
    CAD: 1.3886,
    EUR: 0.86237,
    GBP: 0.7386,
    NZD: 1.6902,
    SGD: 1.2727
  },
  markets: {
    US: 'USD',
    AU: 'AUD',
    CA: 'CAD',
    GB: 'GBP',
    NZ: 'NZD',
    SG: 'SGD',
    DE: 'EUR',
    FR: 'EUR',
    IT: 'EUR',
    ES: 'EUR',
    NL: 'EUR',
    BE: 'EUR',
    IE: 'EUR',
    AT: 'EUR',
    FI: 'EUR',
    PT: 'EUR',
    GR: 'EUR'
  },
  display: {
    label: 'Estimated local price',
    disclaimer: 'Final checkout is charged in USD. Bank exchange rates, card fees, taxes, duties, and shipping charges may vary.'
  }
};

function getBrowserCountry() {
  const locale = navigator.languages?.[0] || navigator.language || '';
  const match = locale.match(/-([A-Z]{2})$/i);
  return match ? match[1].toUpperCase() : '';
}

function getUrlCountry() {
  const country = new URL(window.location.href).searchParams.get('country') || '';
  return /^[A-Z]{2}$/i.test(country) ? country.toUpperCase() : '';
}

function isFresh(payload) {
  const updatedAt = Date.parse(payload.updatedAt);
  const ttlHours = Number(payload.ttlHours || 72);

  if (!Number.isFinite(updatedAt)) return false;

  return Date.now() - updatedAt <= ttlHours * 60 * 60 * 1000;
}

async function requestJson(url) {
  if (typeof window.fetch === 'function') {
    try {
      let timeoutId;
      const timeout = new Promise((_, reject) => {
        timeoutId = window.setTimeout(() => reject(new Error('Rates request timed out')), 3000);
      });
      const response = await Promise.race([
        window.fetch(url, { headers: { accept: 'application/json' } }),
        timeout
      ]);
      window.clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`Rates request failed: ${response.status}`);
      return response.json();
    } catch {
      // Fall through to XHR for storefronts where fetch is blocked by browser extensions or policy.
    }
  }

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', url, true);
    xhr.timeout = 3000;
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Rates request failed: ${xhr.status}`));
        return;
      }

      try {
        resolve(JSON.parse(xhr.responseText));
      } catch (error) {
        reject(error);
      }
    };
    xhr.onerror = () => reject(new Error('Rates request failed'));
    xhr.ontimeout = () => reject(new Error('Rates request timed out'));
    xhr.send();
  });
}

function getScriptUrl(url) {
  const scriptUrl = url.replace(/exchange-rates\.json(\?.*)?$/i, 'exchange-rates.js');
  const separator = scriptUrl.includes('?') ? '&' : '?';
  return `${scriptUrl}${separator}v=20260901-js-endpoint`;
}

async function requestRatesScript(url) {
  if (window.LinQtonExchangeRates) return window.LinQtonExchangeRates;

  const scriptUrl = getScriptUrl(url);

  if (!ratesScriptCache.has(scriptUrl)) {
    ratesScriptCache.set(
      scriptUrl,
      new Promise((resolve) => {
        const existingScript = document.querySelector(`script[src="${scriptUrl}"]`);

        if (existingScript) {
          existingScript.addEventListener('load', () => resolve(window.LinQtonExchangeRates || null), { once: true });
          existingScript.addEventListener('error', () => resolve(null), { once: true });
          window.setTimeout(() => resolve(window.LinQtonExchangeRates || null), 2500);
          return;
        }

        const script = document.createElement('script');
        script.src = scriptUrl;
        script.async = true;
        script.dataset.linqtonRatesScript = 'true';
        script.onload = () => resolve(window.LinQtonExchangeRates || null);
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
      })
    );
  }

  return ratesScriptCache.get(scriptUrl);
}

async function loadRates(url) {
  if (!ratesCache.has(url)) {
    ratesCache.set(
      url,
      requestJson(url)
        .catch(() => requestRatesScript(url))
        .then((payload) => payload || FALLBACK_RATES)
        .catch((error) => {
          ratesCache.delete(url);
          throw error;
        })
    );
  }

  return ratesCache.get(url);
}

function renderEstimateLine(line, label, amount, compact = false) {
  line.textContent = '';

  const amountNode = document.createElement('span');
  amountNode.className = 'local-currency-estimate__amount';
  amountNode.textContent = amount;

  if (!compact) {
    line.append(document.createTextNode(`${label}: `));
  }

  line.append(amountNode);
}

class LocalCurrencyEstimate extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
    this.renderEstimate();
  }

  disconnectedCallback() {
    this.section?.removeEventListener(VARIANT_UPDATE_EVENT, this.handleVariantUpdate);
    this.initialized = false;
  }

  handleVariantUpdate = (event) => {
    const data = event.detail?.data;

    if (!data) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const nextEstimate = data.html?.querySelector('local-currency-estimate');
    if (!nextEstimate) return;

    this.dataset.basePriceCents = nextEstimate.dataset.basePriceCents || this.dataset.basePriceCents;
    this.renderEstimate();
  };

  async renderEstimate() {
    const url = this.dataset.ratesUrl;
    const baseCurrency = this.dataset.baseCurrency;
    const basePrice = Number(this.dataset.basePriceCents) / 100;

    if (!url || baseCurrency !== 'USD' || !Number.isFinite(basePrice) || basePrice <= 0) {
      this.hidden = true;
      return;
    }

    try {
      const payload = await loadRates(url);
      const country = (getUrlCountry() || window.Shopify?.country || this.dataset.country || getBrowserCountry()).toUpperCase();
      const targetCurrency = payload.markets?.[country];
      const rate = Number(payload.rates?.[targetCurrency]);

      if (!isFresh(payload) || !targetCurrency || targetCurrency === baseCurrency || !Number.isFinite(rate) || rate <= 0) {
        this.hidden = true;
        return;
      }

      const estimatedPrice = basePrice * rate;
      const locale = navigator.languages?.[0] || navigator.language || 'en-US';
      const formatter = new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: targetCurrency,
        maximumFractionDigits: estimatedPrice >= 100 ? 0 : 2
      });

      const line = this.querySelector('[data-local-currency-line]');
      const note = this.querySelector('[data-local-currency-note]');

      if (line) {
        renderEstimateLine(
          line,
          payload.display?.label || 'Estimated local price',
          formatter.format(estimatedPrice),
          this.dataset.compact === 'true'
        );
      }

      if (note) {
        note.textContent =
          payload.display?.disclaimer ||
          'Final checkout is charged in USD. Bank exchange rates, card fees, taxes, duties, and shipping charges may vary.';
      }

      this.hidden = false;
    } catch {
      this.hidden = true;
    }
  }
}

if (!customElements.get('local-currency-estimate')) {
  customElements.define('local-currency-estimate', LocalCurrencyEstimate);
}

function renderAllEstimates() {
  document.querySelectorAll('local-currency-estimate').forEach((estimate) => {
    estimate.renderEstimate?.();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', renderAllEstimates, { once: true });
} else {
  renderAllEstimates();
}

window.setTimeout(renderAllEstimates, 500);
window.setTimeout(renderAllEstimates, 1500);
window.setTimeout(renderAllEstimates, 3000);
